import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { IonicModule, ModalController, PopoverController } from '@ionic/angular';
import { LucideAngularModule } from 'lucide-angular';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { IconPickerComponent } from 'src/app/components/icon-picker/icon-picker.component';
import { ICON_MAP, getIconComponent } from 'src/constants/icon-map.constants';
import { KnowledgeBaseCollection } from 'src/models/knowledge-base-collection.model';
import { KnowledgeBaseCollectionsService } from 'src/services/knowledge-base-collections.service';
import { MessageService } from 'src/services/message.service';

@Component({
  selector: 'app-upsert-collection-modal',
  templateUrl: './upsert-collection-modal.component.html',
  styleUrls: ['./upsert-collection-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, ReactiveFormsModule, LucideAngularModule, NgxTippyModule],
})
export class UpsertCollectionModalComponent implements OnInit {
  // Mode determines whether we're creating or editing
  isEditMode = false;
  collection: KnowledgeBaseCollection | null = null;

  collectionForm: FormGroup;
  isLoading = false;
  selectedIcon: string = '';

  // Use shared icon map
  iconMap = ICON_MAP;

  constructor(
    private modalCtrl: ModalController,
    private popoverCtrl: PopoverController,
    private formBuilder: FormBuilder,
    private collectionsService: KnowledgeBaseCollectionsService,
    private messageService: MessageService,
  ) {
    this.collectionForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(1)]],
      description: [''],
    });
  }

  ngOnInit() {
    // Determine if we're in edit mode based on whether collection is provided
    this.isEditMode = !!this.collection;

    // Initialize form with collection data if in edit mode
    if (this.isEditMode && this.collection) {
      this.collectionForm.patchValue({
        name: this.collection.name,
        description: this.collection.description || '',
      });
      this.selectedIcon = this.collection.icon || '';
    }
  }

  // Get the icon component for a given icon name
  getIconComponent(iconName: string) {
    return getIconComponent(iconName);
  }

  // Getter for the current icon component
  get currentIconComponent() {
    if (!this.selectedIcon) return null;
    return getIconComponent(this.selectedIcon);
  }

  async selectIcon() {
    const popover = await this.popoverCtrl.create({
      component: IconPickerComponent,
      componentProps: {
        selectedIcon: this.selectedIcon,
      },
    });

    popover.onDidDismiss().then((result) => {
      if (result.data) {
        this.selectedIcon = result.data;
      }
    });

    await popover.present();
  }

  async onSubmit() {
    if (this.collectionForm.valid && !this.isLoading) {
      this.isLoading = true;
      try {
        const formData = this.collectionForm.value;

        if (this.isEditMode && this.collection) {
          // Update existing collection
          await this.collectionsService.update(this.collection.id, {
            name: formData.name.trim(),
            description: formData.description?.trim() || null,
            icon: this.selectedIcon || null,
          });
        } else {
          // Create new collection
          await this.collectionsService.create({
            name: formData.name.trim(),
            description: formData.description?.trim() || null,
            icon: this.selectedIcon || null,
            parentId: this.parentId,
            projectId: this.projectId,
          });
        }

        this.modalCtrl.dismiss({ success: true });
      } catch (error) {
        console.error(`${this.isEditMode ? 'Update' : 'Create'} collection failed:`, error);
        this.messageService.showError(error);
      } finally {
        this.isLoading = false;
      }
    }
  }

  onCancel() {
    this.modalCtrl.dismiss({ success: false });
  }

  // These will be set by the parent component via componentProps
  parentId: string | null = null;
  projectId: string | null = null;
}
